import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
  Box,
  Typography,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { GearTshirt, GearShorts, GearSocks, GearDesign, GearOption } from "../types";
import DesignPicker from "./GearDesignPicker";

interface GearOrderEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  // Kept generic (rather than hardcoding apiRequest) so this dialog can be reused
  // both by the player dashboard (player-token auth) and the pending-registrant
  // standalone page (its own narrow registration-token auth).
  fetchFn: (endpoint: string, options?: RequestInit) => Promise<Response>;
  getEndpoint: string;
  updateEndpoint: string;
}

export default function GearOrderEditDialog({
  open,
  onClose,
  onSuccess,
  fetchFn,
  getEndpoint,
  updateEndpoint,
}: GearOrderEditDialogProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [gearEditingEnabled, setGearEditingEnabled] = useState(true);

  const [wantsGear, setWantsGear] = useState(false);
  const [gearTshirts, setGearTshirts] = useState<GearTshirt[]>([]);
  const [gearShorts, setGearShorts] = useState<GearShorts[]>([]);
  const [gearSocks, setGearSocks] = useState<GearSocks[]>([]);

  const [gearDesigns, setGearDesigns] = useState<GearDesign[]>([]);
  const tshirtDesigns = gearDesigns.filter(
    (d) => (d.category || "tshirt") === "tshirt",
  );
  const poloDesigns = gearDesigns.filter((d) => d.category === "polo");
  const shortsDesigns = gearDesigns.filter((d) => d.category === "shorts");
  const [tshirtPrice, setTshirtPrice] = useState<number | null>(null);
  const [shortsPrice, setShortsPrice] = useState<number | null>(null);
  const [kitPrice, setKitPrice] = useState<number | null>(null);
  const [tshirtSizeChartUrl, setTshirtSizeChartUrl] = useState("");
  const [poloSizeChartUrl, setPoloSizeChartUrl] = useState("");
  const [shortsSizeChartUrl, setShortsSizeChartUrl] = useState("");

  useEffect(() => {
    if (!open) return;

    const load = async () => {
      setLoading(true);
      setError("");
      setSuccess(false);
      try {
        const [orderRes, statusRes, designsRes] = await Promise.all([
          fetchFn(getEndpoint),
          fetchFn("/api/season-registration/status"),
          fetchFn("/api/season-registration/gear-designs"),
        ]);

        if (orderRes.ok) {
          const data = await orderRes.json();
          setGearEditingEnabled(data.gearEditingEnabled ?? false);
          setWantsGear((data.gearOption || "none") !== "none");
          setGearTshirts(data.gearTshirts || []);
          setGearShorts(data.gearShorts || []);
          setGearSocks(data.gearSocks || []);
        } else {
          const errData = await orderRes.json().catch(() => ({}));
          setError(errData.error || "Failed to load your gear order");
        }

        if (statusRes.ok) {
          const statusData = await statusRes.json();
          setTshirtPrice(statusData.tshirtPrice ?? null);
          setShortsPrice(statusData.shortsPrice ?? null);
          setKitPrice(statusData.kitPrice ?? null);
          setTshirtSizeChartUrl(statusData.tshirtSizeChartUrl || "");
          setPoloSizeChartUrl(statusData.poloSizeChartUrl || "");
          setShortsSizeChartUrl(statusData.shortsSizeChartUrl || "");
        }

        if (designsRes.ok) {
          setGearDesigns(await designsRes.json());
        }
      } catch (err) {
        console.error("Error loading gear order:", err);
        setError("Failed to load your gear order");
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, getEndpoint]);

  const addTshirt = () => {
    setGearTshirts([
      ...gearTshirts,
      { size: "M", shirtType: "tshirt", nameOnShirt: "" },
    ]);
  };

  const removeTshirt = (index: number) => {
    setGearTshirts(gearTshirts.filter((_, i) => i !== index));
  };

  const updateTshirt = (
    index: number,
    field: keyof GearTshirt,
    value: string,
  ) => {
    const updated = [...gearTshirts];
    updated[index] = { ...updated[index], [field]: value };
    // The design catalog is split by shirt type, so a design picked under one
    // type never carries over as a valid designId under the other.
    if (field === "shirtType") {
      delete updated[index].designId;
      delete updated[index].designName;
    }
    setGearTshirts(updated);
  };

  const updateTshirtDesign = (index: number, designId: string) => {
    const design = (
      gearTshirts[index]?.shirtType === "polo" ? poloDesigns : tshirtDesigns
    ).find((d) => d.id === designId);
    const updated = [...gearTshirts];
    updated[index] = {
      ...updated[index],
      designId: design?.id,
      designName: design?.name,
    };
    setGearTshirts(updated);
  };

  const addShorts = () => {
    setGearShorts([...gearShorts, { size: "M" }]);
    setGearSocks([...gearSocks, { size: "" }]);
  };

  const removeShorts = (index: number) => {
    setGearShorts(gearShorts.filter((_, i) => i !== index));
    setGearSocks(gearSocks.filter((_, i) => i !== index));
  };

  const updateShorts = (
    index: number,
    size: "S" | "M" | "L" | "XL" | "XXL",
  ) => {
    const updated = [...gearShorts];
    updated[index] = { ...updated[index], size };
    setGearShorts(updated);
  };

  const updateShortsDesign = (index: number, designId: string) => {
    const design = shortsDesigns.find((d) => d.id === designId);
    const updated = [...gearShorts];
    updated[index] = {
      ...updated[index],
      designId: design?.id,
      designName: design?.name,
    };
    setGearShorts(updated);
  };

  const updateSocks = (index: number, size: string) => {
    const updated = [...gearSocks];
    updated[index] = { size };
    setGearSocks(updated);
  };

  const isKitCombo =
    gearTshirts.length > 0 && gearShorts.length > 0 && gearSocks.length > 0;

  const calculateGearTotal = (): number => {
    if (!wantsGear) return 0;
    let total = 0;
    if (isKitCombo) {
      total += kitPrice || 0;
      total += Math.max(0, gearTshirts.length - 1) * (tshirtPrice || 0);
      total += Math.max(0, gearShorts.length - 1) * (shortsPrice || 0);
    } else {
      total += gearTshirts.length * (tshirtPrice || 0);
      total += gearShorts.length * (shortsPrice || 0);
    }
    return total;
  };

  const computeGearOption = (): GearOption => {
    if (!wantsGear) return "none";
    if (isKitCombo) return "full-kit";
    if (gearTshirts.length > 0 && gearShorts.length === 0) return "tshirt-only";
    if (gearShorts.length > 0 && gearTshirts.length === 0) return "shorts-only";
    return "full-kit";
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const response = await fetchFn(updateEndpoint, {
        method: "PUT",
        body: JSON.stringify({
          gearOption: computeGearOption(),
          gearTshirts: wantsGear ? gearTshirts : [],
          gearShorts: wantsGear ? gearShorts : [],
          gearSocks: wantsGear ? gearSocks : [],
          gearTotalCost: calculateGearTotal(),
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || "Failed to update gear order");
      }

      setSuccess(true);
      onSuccess?.();
      setTimeout(() => {
        onClose();
      }, 1200);
    } catch (err: any) {
      setError(err.message || "Failed to update gear order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Modify Your Gear Order</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !gearEditingEnabled ? (
          <Alert severity="info">
            Gear order editing is not available right now.
          </Alert>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Gear order updated successfully!
              </Alert>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={wantsGear}
                  onChange={(e) => {
                    setWantsGear(e.target.checked);
                    if (e.target.checked && gearTshirts.length === 0) {
                      setGearTshirts([
                        { size: "M", shirtType: "tshirt", nameOnShirt: "" },
                      ]);
                    }
                  }}
                />
              }
              label="I want to order official Stuttgart Padel League gear 🎽"
              sx={{ mb: 2, alignItems: "flex-start" }}
            />

            {wantsGear && (
              <Box
                sx={{ mb: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}
              >
                <Alert severity="info" sx={{ mb: 2 }}>
                  💡 <strong>Smart pricing:</strong> Order a shirt + shorts/skirt +
                  socks together and save! Kit:{" "}
                  {kitPrice ? `${kitPrice}€` : "TBD"} vs individual shirt (
                  {tshirtPrice ? `${tshirtPrice}€` : "TBD"}) + shorts/skirt+socks (
                  {shortsPrice ? `${shortsPrice}€` : "TBD"}).
                </Alert>

                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  👕 Shirts
                </Typography>

                {gearTshirts.map((tshirt, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 2,
                      p: 1.5,
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <ToggleButtonGroup
                        value={tshirt.shirtType || "tshirt"}
                        exclusive
                        onChange={(_, val) => {
                          if (val) updateTshirt(index, "shirtType", val);
                        }}
                        size="small"
                      >
                        <ToggleButton
                          value="tshirt"
                          sx={{ px: 2, textTransform: "none", fontWeight: 600 }}
                        >
                          👕 T-Shirt
                        </ToggleButton>
                        <ToggleButton
                          value="polo"
                          sx={{ px: 2, textTransform: "none", fontWeight: 600 }}
                        >
                          🏇 Polo
                        </ToggleButton>
                      </ToggleButtonGroup>
                      <IconButton
                        onClick={() => removeTshirt(index)}
                        color="error"
                        disabled={gearTshirts.length === 1}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <DesignPicker
                        designs={
                          tshirt.shirtType === "polo"
                            ? poloDesigns
                            : tshirtDesigns
                        }
                        selectedId={tshirt.designId}
                        onSelect={(id) => updateTshirtDesign(index, id)}
                        label="Design"
                        sizeChartUrl={
                          tshirt.shirtType === "polo"
                            ? poloSizeChartUrl
                            : tshirtSizeChartUrl
                        }
                      />
                      <FormControl sx={{ minWidth: 80 }}>
                        <InputLabel>Size</InputLabel>
                        <Select
                          value={tshirt.size}
                          onChange={(e) =>
                            updateTshirt(index, "size", e.target.value)
                          }
                          label="Size"
                          size="small"
                        >
                          <MenuItem value="S">S</MenuItem>
                          <MenuItem value="M">M</MenuItem>
                          <MenuItem value="L">L</MenuItem>
                          <MenuItem value="XL">XL</MenuItem>
                          <MenuItem value="XXL">XXL</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        label="Name on Shirt (Optional)"
                        value={tshirt.nameOnShirt || ""}
                        onChange={(e) =>
                          updateTshirt(index, "nameOnShirt", e.target.value)
                        }
                        size="small"
                        inputProps={{ maxLength: 20 }}
                      />
                    </Box>
                  </Box>
                ))}

                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addTshirt}
                  size="small"
                  sx={{ mb: 3 }}
                >
                  Add Another Shirt
                </Button>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  🩳 Shorts/Skirt + Socks (optional)
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Each shorts/skirt comes with a pair of socks. Add shorts/skirt
                  to combine with a shirt for kit pricing.
                </Typography>

                {gearShorts.length === 0 ? (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addShorts}
                    size="small"
                    sx={{ mb: 2 }}
                  >
                    Add Shorts/Skirt + Socks
                  </Button>
                ) : (
                  <>
                    {gearShorts.map((shorts, index) => (
                      <Box
                        key={index}
                        sx={{
                          mb: 2,
                          p: 1.5,
                          bgcolor: "background.paper",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <DesignPicker
                            designs={shortsDesigns}
                            selectedId={shorts.designId}
                            onSelect={(id) => updateShortsDesign(index, id)}
                            label="Design"
                            sizeChartUrl={shortsSizeChartUrl}
                          />
                          <Box
                            sx={{ display: "flex", gap: 1, alignItems: "center" }}
                          >
                            <FormControl sx={{ flex: 1 }}>
                              <InputLabel>Shorts/Skirt Size</InputLabel>
                              <Select
                                value={shorts.size}
                                onChange={(e) =>
                                  updateShorts(index, e.target.value as any)
                                }
                                label="Shorts/Skirt Size"
                                size="small"
                              >
                                <MenuItem value="S">S</MenuItem>
                                <MenuItem value="M">M</MenuItem>
                                <MenuItem value="L">L</MenuItem>
                                <MenuItem value="XL">XL</MenuItem>
                                <MenuItem value="XXL">XXL</MenuItem>
                              </Select>
                            </FormControl>
                            <IconButton
                              onClick={() => removeShorts(index)}
                              color="error"
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                        <TextField
                          fullWidth
                          label="🧦 Socks - Shoe Size"
                          value={gearSocks[index]?.size || ""}
                          onChange={(e) => updateSocks(index, e.target.value)}
                          size="small"
                          placeholder="e.g., 42"
                          inputProps={{
                            maxLength: 2,
                            pattern: "[0-9]*",
                          }}
                          helperText="Included with shorts/skirt"
                        />
                      </Box>
                    ))}

                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={addShorts}
                      size="small"
                      sx={{ mb: 2 }}
                    >
                      Add Another Shorts/Skirt + Socks
                    </Button>
                  </>
                )}

                {(gearTshirts.length > 0 || gearShorts.length > 0) && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    {isKitCombo && (
                      <Alert severity="success" sx={{ mb: 1 }}>
                        🎉 <strong>Kit discount applied!</strong> First shirt +
                        shorts/skirt + socks bundled at {kitPrice}€.
                      </Alert>
                    )}
                    <Alert severity="info">
                      💰 Estimated Gear Total:{" "}
                      <strong>{calculateGearTotal()}€</strong>
                    </Alert>
                  </>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={
            loading ||
            saving ||
            !gearEditingEnabled ||
            (wantsGear && gearTshirts.some((t) => !t.designId)) ||
            (wantsGear && gearShorts.some((s) => !s.designId))
          }
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
